/*============================================================================
  HC - IB brands page controller - 2026-09-27

  Drives sections/ib-brands-page.liquid (the /pages/brands directory). Loaded
  only by that section. The page is complete without it: the list is sorted in
  Liquid, the A-Z bar is #anchors and the search is a /search form. This adds:
  - brand search as you type: rows filter in place, empty letters and the
    Elite showcase step aside, Enter opens the first match (or searches
    products when no brand matches), Escape clears, the arrow keys walk the
    results;
  - the A-Z bar: a smooth jump that lands under the sticky toolbar, the letter
    under the toolbar lit as the page scrolls, letters with nothing left
    disabled;
  - the toolbar sticks under the header's visible bottom edge, measured live
    like the collection filters' bar (assets/ib-filters.js stickyTop);
  - region hiding: the brands window.IBBrandRegions.blocked() reports for the
    shopper's region (assets/ib-brand-menu-regions.js, which hides the same
    brands in the menus) are hidden here too, and the counts follow.

  Exported on window.IBBrandsPage for the tests: the pure helpers and the live
  instances. ES5 on purpose (no build step).
==============================================================================*/
(function () {
  'use strict';

  var ROOT = '[data-ib-bp]';
  var FILTER_DEBOUNCE_MS = 80;
  var REGION_ATTR = 'data-ib-region-hidden';
  // Space between the toolbar's bottom edge and a letter it jumps to.
  var JUMP_GAP = 8;
  // A letter lights once its section is this close under the toolbar, so
  // the letter whose heading is at the top of the list is the lit one, not
  // the one that just scrolled away above it.
  var SPY_SLACK = 56;
  // A jump is checked once the page has been still this long: the theme
  // compresses the header as the page leaves the top, which moves the target
  // after the scroll was aimed.
  var SETTLE_MS = 180;
  var SETTLE_TRIES = 3;

  /* ------------------------------------------------------------------------
     Pure helpers (exported for tests). Same normalization as the mobile
     menu's brand filter (assets/ib-mobile-menu.js).
  ------------------------------------------------------------------------ */

  // "L'Oreal" -> "loreal", "äz" -> "az", "milk_shake" -> "milkshake".
  function normalizeText(value) {
    var s = value === null || value === undefined ? '' : String(value);
    if (typeof s.normalize === 'function') {
      s = s.normalize('NFD').replace(/[̀-ͯ]/g, '');
    }
    s = s.toLowerCase();
    s = s.replace(/&amp;/g, ' ');
    s = s.replace(/[^a-z0-9 ]+/g, '');
    s = s.replace(/\s+/g, ' ').replace(/^ | $/g, '');
    return s;
  }

  // Both arguments already normalized. An empty query matches everything. One
  // character matches word starts only ("k": Kasho, Kevin Murphy, not Babe
  // Hair Extensions); longer queries match anywhere, spaces optional.
  function matchesQuery(title, query) {
    var t = String(title || '');
    var q = String(query || '');
    if (!q) return true;
    var words = t.split(' ');
    var i;
    if (q.length === 1) {
      for (i = 0; i < words.length; i++) {
        if (words[i].charAt(0) === q) return true;
      }
      return false;
    }
    if (t.indexOf(q) !== -1) return true;
    var squashed = q.replace(/ /g, '');
    return Boolean(squashed) && t.replace(/ /g, '').indexOf(squashed) !== -1;
  }

  function brandLabel(n) {
    return n + (n === 1 ? ' brand' : ' brands');
  }

  function toArray(list) {
    var out = [];
    if (!list) return out;
    for (var i = 0; i < list.length; i++) out.push(list[i]);
    return out;
  }

  function reducedMotion() {
    try {
      return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    } catch (e) {
      return false;
    }
  }

  function scrollY() {
    return window.pageYOffset || (document.documentElement && document.documentElement.scrollTop) || 0;
  }

  function scrollWindowTo(top, smooth) {
    top = Math.max(0, Math.round(top));
    try {
      window.scrollTo({ top: top, behavior: smooth ? 'smooth' : 'auto' });
    } catch (e) {
      window.scrollTo(0, top);
    }
  }

  /* ------------------------------------------------------------------------
     Controller
  ------------------------------------------------------------------------ */

  function BrandsPage(root) {
    var self = this;
    this.root = root;
    this.listeners = [];
    this.raw = '';
    this.query = '';
    this.first = null;
    this.visible = 0;
    this.current = null;
    this.top = -1;
    this.barH = -1;
    this.timer = null;
    this.frame = null;
    this.pending = null;
    this.settleTimer = null;
    this.settleTries = 0;
    this.destroyed = false;

    this.hero = root.querySelector('.ib-bp__hero');
    this.toolbar = root.querySelector('[data-ib-bp-toolbar]');
    this.form = root.querySelector('[data-ib-bp-form]');
    this.input = root.querySelector('[data-ib-bp-search]');
    this.clearBtn = root.querySelector('[data-ib-bp-clear]');
    this.status = root.querySelector('[data-ib-bp-status]');
    this.az = root.querySelector('[data-ib-bp-az]');
    this.jumps = toArray(root.querySelectorAll('[data-ib-bp-jump]'));
    this.elite = root.querySelector('[data-ib-bp-elite]');
    this.all = root.querySelector('.ib-bp__all');
    this.sections = toArray(root.querySelectorAll('[data-ib-bp-letter]'));
    this.empty = root.querySelector('[data-ib-bp-empty]');
    this.queryEl = root.querySelector('[data-ib-bp-query]');
    this.searchAll = root.querySelector('[data-ib-bp-search-all]');
    this.counts = toArray(root.querySelectorAll('[data-ib-bp-count]'));
    this.shown = root.querySelector('[data-ib-bp-shown]');
    this.allTitle = root.querySelector('[data-ib-bp-all-title]');

    // One entry per brand row: its normalized title, link and letter.
    this.rows = toArray(root.querySelectorAll('[data-ib-bp-brand]')).map(function (el) {
      var section = el.closest('[data-ib-bp-letter]');
      return {
        el: el,
        link: el.querySelector('a'),
        key: normalizeText(el.getAttribute('data-ib-bp-title') || el.textContent),
        letter: section ? section.getAttribute('data-ib-bp-letter') : ''
      };
    });
    this.tiles = toArray(root.querySelectorAll('[data-ib-bp-tile]'));

    this.onScroll = function () {
      if (self.pending) self.armSettle();
      self.schedule();
    };
    // The shopper scrolling during a jump takes over from it.
    this.onUserScroll = function () {
      self.pending = null;
      clearTimeout(self.settleTimer);
    };
  }

  BrandsPage.prototype = {
    init: function () {
      this.bind();
      this.measure();
      // A value the browser kept (Back, a restored tab) filters straight away.
      this.apply(this.input ? this.input.value : '', { scroll: false });
      this.initRegions();
    },

    listen: function (target, type, fn, opts) {
      if (!target || typeof target.addEventListener !== 'function') return;
      target.addEventListener(type, fn, opts);
      this.listeners.push({ target: target, type: type, fn: fn, opts: opts });
    },

    destroy: function () {
      this.destroyed = true;
      clearTimeout(this.timer);
      clearTimeout(this.settleTimer);
      for (var i = 0; i < this.listeners.length; i++) {
        var l = this.listeners[i];
        l.target.removeEventListener(l.type, l.fn, l.opts);
      }
      this.listeners = [];
    },

    bind: function () {
      var self = this;

      this.listen(this.input, 'input', function () {
        clearTimeout(self.timer);
        self.timer = setTimeout(function () {
          self.apply(self.input.value, { scroll: true });
        }, FILTER_DEBOUNCE_MS);
      });

      // The native clear (x) and Enter on some keyboards fire 'search'.
      this.listen(this.input, 'search', function () {
        clearTimeout(self.timer);
        self.apply(self.input.value, { scroll: true });
      });

      this.listen(this.input, 'keydown', function (evt) {
        if (evt.key === 'Escape' || evt.key === 'Esc') {
          if (!self.input.value) return;
          evt.preventDefault();
          self.clear(true);
        } else if (evt.key === 'ArrowDown' || evt.key === 'Down') {
          var links = self.visibleLinks();
          if (!links.length) return;
          evt.preventDefault();
          links[0].focus();
        }
      });

      this.listen(this.clearBtn, 'click', function () {
        self.clear(true);
      });

      this.listen(this.form, 'submit', function (evt) {
        self.onSubmit(evt);
      });

      // Up/Down walk the visible brands; Up from the first returns to search.
      this.listen(this.all, 'keydown', function (evt) {
        var key = evt.key;
        if (key !== 'ArrowDown' && key !== 'ArrowUp' && key !== 'Down' && key !== 'Up') return;
        var links = self.visibleLinks();
        var at = links.indexOf(document.activeElement);
        if (at === -1) return;
        evt.preventDefault();
        var down = key === 'ArrowDown' || key === 'Down';
        if (down && at < links.length - 1) {
          links[at + 1].focus();
        } else if (!down) {
          if (at > 0) links[at - 1].focus();
          else if (self.input) self.input.focus();
        }
      });

      this.listen(this.az, 'click', function (evt) {
        var jump = evt.target && evt.target.closest ? evt.target.closest('[data-ib-bp-jump]') : null;
        if (!jump) return;
        evt.preventDefault();
        if (jump.getAttribute('aria-disabled') === 'true') return;
        self.jumpTo(jump.getAttribute('data-ib-bp-jump'));
      });

      this.listen(window, 'scroll', this.onScroll, { passive: true });
      this.listen(window, 'resize', this.onScroll);
      this.listen(window, 'wheel', this.onUserScroll, { passive: true });
      this.listen(window, 'touchstart', this.onUserScroll, { passive: true });
      // theme.js: the sticky header compressed or expanded.
      this.listen(document, 'headerStickyChange', this.onScroll);
    },

    /* ---------------- search ---------------- */

    clear: function (refocus) {
      clearTimeout(this.timer);
      if (this.input) {
        this.input.value = '';
        if (refocus) this.input.focus();
      }
      this.apply('', { scroll: true });
    },

    onSubmit: function (evt) {
      clearTimeout(this.timer);
      var raw = this.input ? this.input.value : '';
      if (!normalizeText(raw)) {
        evt.preventDefault();
        return;
      }
      this.apply(raw, { scroll: false });
      if (this.first) {
        evt.preventDefault();
        var link = this.first.querySelector('a');
        if (link) window.location.href = link.getAttribute('href');
      }
      // No brand matches: the form goes on to search products.
    },

    apply: function (raw, opts) {
      opts = opts || {};
      this.raw = String(raw || '');
      var q = normalizeText(this.raw);
      this.query = q;

      var perLetter = {};
      var visible = 0;
      var first = null;
      for (var i = 0; i < this.rows.length; i++) {
        var row = this.rows[i];
        var show = !row.el.hasAttribute(REGION_ATTR) && matchesQuery(row.key, q);
        row.el.hidden = !show;
        row.el.classList.remove('is-first');
        if (!show) continue;
        visible += 1;
        perLetter[row.letter] = (perLetter[row.letter] || 0) + 1;
        if (!first) first = row.el;
      }
      this.visible = visible;
      this.first = q ? first : null;
      if (this.first) this.first.classList.add('is-first');

      for (var s = 0; s < this.sections.length; s++) {
        var letter = this.sections[s].getAttribute('data-ib-bp-letter');
        this.sections[s].hidden = !perLetter[letter];
      }

      for (var j = 0; j < this.jumps.length; j++) {
        var jump = this.jumps[j];
        var on = Boolean(perLetter[jump.getAttribute('data-ib-bp-jump')]);
        var section = on ? this.sectionFor(jump.getAttribute('data-ib-bp-jump')) : null;
        if (on && section) {
          jump.setAttribute('href', '#' + section.id);
          jump.removeAttribute('aria-disabled');
        } else {
          jump.removeAttribute('href');
          jump.setAttribute('aria-disabled', 'true');
        }
      }

      // Elite cards: region hiding only. A search hides the whole showcase,
      // since every Elite brand is in the list below too.
      var eliteLeft = 0;
      for (var t = 0; t < this.tiles.length; t++) {
        var gone = this.tiles[t].hasAttribute(REGION_ATTR);
        this.tiles[t].hidden = gone;
        if (!gone) eliteLeft += 1;
      }
      if (this.elite) this.elite.hidden = Boolean(q) || eliteLeft === 0;

      if (this.empty) this.empty.hidden = !(q && visible === 0);
      if (this.queryEl) this.queryEl.textContent = this.raw.replace(/^\s+|\s+$/g, '');
      if (this.searchAll) this.searchAll.setAttribute('href', this.productSearchUrl(this.raw));
      if (this.clearBtn) this.clearBtn.hidden = !this.raw;
      // "All brands 91" becomes "Search results 3" while a search is on.
      if (this.shown) this.shown.textContent = String(visible);
      if (this.allTitle) {
        var label = this.allTitle.getAttribute(q ? 'data-ib-bp-label-search' : 'data-ib-bp-label');
        if (label && this.allTitle.textContent !== label) this.allTitle.textContent = label;
      }
      if (this.status) {
        var text = q ? (visible ? brandLabel(visible) : 'No brands match') : '';
        if (this.status.textContent !== text) this.status.textContent = text;
      }

      if (opts.scroll) this.keepResultsInView();
      this.sync();
    },

    // The product search the empty state links to: the same request the form
    // sends, built from its own fields.
    productSearchUrl: function (raw) {
      var action = (this.form && this.form.getAttribute('action')) || '/search';
      var parts = ['q=' + encodeURIComponent(String(raw || '').replace(/^\s+|\s+$/g, ''))];
      var hidden = this.form ? toArray(this.form.querySelectorAll('input[type="hidden"]')) : [];
      for (var i = 0; i < hidden.length; i++) {
        var name = hidden[i].getAttribute('name');
        if (name) parts.push(encodeURIComponent(name) + '=' + encodeURIComponent(hidden[i].value || hidden[i].getAttribute('value') || ''));
      }
      return action + '?' + parts.join('&');
    },

    visibleLinks: function () {
      var out = [];
      for (var i = 0; i < this.rows.length; i++) {
        if (!this.rows[i].el.hidden && this.rows[i].link) out.push(this.rows[i].link);
      }
      return out;
    },

    // Typing with the list scrolled under the toolbar: bring the results'
    // start back up to it, or the matches would sit above the screen.
    keepResultsInView: function () {
      if (!this.all) return;
      this.measure();
      var line = this.top + this.barH;
      var at = this.all.getBoundingClientRect().top;
      if (at < line) scrollWindowTo(scrollY() + at - line, false);
    },

    /* ---------------- A-Z ---------------- */

    sectionFor: function (letter) {
      for (var i = 0; i < this.sections.length; i++) {
        if (this.sections[i].getAttribute('data-ib-bp-letter') === letter) return this.sections[i];
      }
      return null;
    },

    jumpTo: function (letter) {
      var section = this.sectionFor(letter);
      if (!section || section.hidden) return;
      this.pending = letter;
      this.settleTries = 0;
      this.setCurrent(letter);
      scrollWindowTo(scrollY() + this.offsetOf(section), !reducedMotion());
      this.armSettle();
    },

    // How far the page has to move for a section to sit under the toolbar.
    offsetOf: function (section) {
      this.measure();
      return section.getBoundingClientRect().top - (this.top + this.barH) - JUMP_GAP;
    },

    armSettle: function () {
      var self = this;
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(function () {
        self.settle();
      }, SETTLE_MS);
    },

    // The page has stopped: correct the landing if the header moved it.
    settle: function () {
      var section = this.pending ? this.sectionFor(this.pending) : null;
      if (!section || this.destroyed) {
        this.pending = null;
        return;
      }
      var delta = this.offsetOf(section);
      if (Math.abs(delta) > 2 && this.settleTries < SETTLE_TRIES) {
        this.settleTries += 1;
        scrollWindowTo(scrollY() + delta, false);
        this.armSettle();
        return;
      }
      this.pending = null;
      this.sync();
    },

    setCurrent: function (letter) {
      if (letter === this.current) return;
      this.current = letter;
      var lit = null;
      for (var i = 0; i < this.jumps.length; i++) {
        var on = this.jumps[i].getAttribute('data-ib-bp-jump') === letter;
        this.jumps[i].classList.toggle('is-current', on);
        if (on) lit = this.jumps[i];
      }
      if (lit) this.reveal(lit);
    },

    // Phones: the letter strip scrolls sideways; keep the lit letter centred.
    reveal: function (jump) {
      var az = this.az;
      if (!az || !(az.scrollWidth > az.clientWidth + 1)) return;
      var left = Math.max(0, jump.offsetLeft - (az.clientWidth - jump.offsetWidth) / 2);
      if (typeof az.scrollTo === 'function') {
        try {
          az.scrollTo({ left: left, behavior: reducedMotion() ? 'auto' : 'smooth' });
          return;
        } catch (e) {
          /* fall through */
        }
      }
      az.scrollLeft = left;
    },

    // The last letter whose section has come within SPY_SLACK of the
    // toolbar's bottom edge. None while the page is still above the list.
    updateCurrent: function () {
      if (this.pending) return;
      var line = this.top + this.barH + SPY_SLACK;
      var current = null;
      for (var i = 0; i < this.sections.length; i++) {
        var section = this.sections[i];
        if (section.hidden) continue;
        if (section.getBoundingClientRect().top > line) break;
        current = section.getAttribute('data-ib-bp-letter');
      }
      this.setCurrent(current);
    },

    /* ---------------- sticky toolbar ---------------- */

    // The toolbar sticks to the bottom edge of the visible header, measured
    // live: a sticky header (constant), one that scrolls away (goes to 0) and
    // the compressed header (shrinks) all come out of the one number.
    measure: function () {
      var header = document.getElementById('SiteHeader');
      var top = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom) - 1) : 0;
      var style = this.root.style;
      if (top !== this.top) {
        this.top = top;
        if (style && typeof style.setProperty === 'function') style.setProperty('--ibbp-top', top + 'px');
      }
      var barH = this.toolbar ? Math.round(this.toolbar.getBoundingClientRect().height) : 0;
      if (barH !== this.barH) {
        this.barH = barH;
        if (style && typeof style.setProperty === 'function') style.setProperty('--ibbp-bar-h', barH + 'px');
      }
    },

    schedule: function () {
      var self = this;
      if (this.frame) return;
      var raf = window.requestAnimationFrame || function (fn) {
        return setTimeout(fn, 16);
      };
      this.frame = raf(function () {
        self.frame = null;
        if (!self.destroyed) self.sync();
      });
    },

    sync: function () {
      this.measure();
      if (this.toolbar && this.hero) {
        var stuck = this.hero.getBoundingClientRect().bottom <= this.top + 1;
        this.toolbar.classList.toggle('is-stuck', stuck);
      }
      this.updateCurrent();
    },

    /* ---------------- region hiding ---------------- */

    initRegions: function () {
      var self = this;
      var regions = window.IBBrandRegions;
      if (!regions || typeof regions.blocked !== 'function') return;
      regions
        .blocked()
        .then(function (blocked) {
          if (self.destroyed || !blocked) return;
          var changed = false;
          var els = self.rows.map(function (row) {
            return row.el;
          }).concat(self.tiles);
          for (var i = 0; i < els.length; i++) {
            var handle = els[i].getAttribute('data-ib-bp-handle');
            if (handle && blocked[handle] === true) {
              els[i].setAttribute(REGION_ATTR, '1');
              changed = true;
            }
          }
          if (!changed) return;
          self.recount();
          self.apply(self.raw, { scroll: false });
        })
        .catch(function () {
          /* degrade to every brand */
        });
    },

    // The brand count (the intro and the search placeholder) counts only the
    // brands this shopper can see. The All brands pill follows in apply().
    recount: function () {
      var n = 0;
      for (var i = 0; i < this.rows.length; i++) {
        if (!this.rows[i].el.hasAttribute(REGION_ATTR)) n += 1;
      }
      for (var c = 0; c < this.counts.length; c++) this.counts[c].textContent = String(n);
      if (this.input) {
        var tpl = this.input.getAttribute('data-ib-bp-placeholder');
        if (tpl) this.input.setAttribute('placeholder', tpl.replace('[count]', n));
      }
    }
  };

  /* ------------------------------------------------------------------------
     Boot, plus the theme editor's section reloads
  ------------------------------------------------------------------------ */

  var instances = [];

  function initWithin(scope) {
    var roots = scope.matches && scope.matches(ROOT) ? [scope] : toArray(scope.querySelectorAll(ROOT));
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].__ibbp) continue;
      var page = new BrandsPage(roots[i]);
      roots[i].__ibbp = page;
      instances.push(page);
      page.init();
    }
  }

  function destroyWithin(scope) {
    for (var i = instances.length - 1; i >= 0; i--) {
      var page = instances[i];
      if (scope === page.root || (scope.contains && scope.contains(page.root))) {
        page.destroy();
        page.root.__ibbp = null;
        instances.splice(i, 1);
      }
    }
  }

  function boot() {
    initWithin(document);
  }

  document.addEventListener('shopify:section:load', function (evt) {
    if (evt.target) initWithin(evt.target);
  });
  document.addEventListener('shopify:section:unload', function (evt) {
    if (evt.target) destroyWithin(evt.target);
  });

  window.IBBrandsPage = {
    normalizeText: normalizeText,
    matchesQuery: matchesQuery,
    BrandsPage: BrandsPage,
    instances: instances,
    init: initWithin
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
